import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { onIdTokenChanged } from "firebase/auth";
import { auth } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { DEFAULT_PERMISSIONS_BY_BASE_ROLE } from "auth/permissions";

export const ROLES = {
  SUPER_ADMIN:  "super_admin",
  TENANT_ADMIN: "tenant_admin",
  SUPERVISOR:   "supervisor",
  USER:         "user",
  VISITOR:      "visitor",
};

const AuthzContext = createContext(null);
const SUPER_ADMIN_UID_OVERRIDES = new Set(["dDC2DCBYglNggwiyRfXVIWXzgFJ2"]);

function normalizeRole(role) {
  if (role === ROLES.SUPER_ADMIN || role === ROLES.TENANT_ADMIN || role === ROLES.SUPERVISOR || role === ROLES.USER || role === ROLES.VISITOR) {
    return role;
  }
  return ROLES.USER;
}

export function AuthzProvider({ children }) {
  const [state, setState] = useState({
    isLoading: true,
    isAuthenticated: false,
    user: null,
    role: ROLES.USER,
    tenantId: null,
    permissions: [],
    claims: {},
  });

  const resolvePermissions = async ({ firebaseUser, claims, role, tenantId }) => {
    const claimPermissions = Array.isArray(claims.permissions) ? claims.permissions : [];
    const fallback = DEFAULT_PERMISSIONS_BY_BASE_ROLE[role] || [];

    if (role === ROLES.SUPER_ADMIN || !tenantId) {
      return claimPermissions.length > 0 ? claimPermissions : fallback;
    }

    try {
      const userDoc = await getDoc(doc(db, "companies", tenantId, "users", firebaseUser.uid));
      const docPermissions = userDoc.exists() && Array.isArray(userDoc.data()?.permissions)
        ? userDoc.data().permissions
        : [];

      return [...new Set([...fallback, ...claimPermissions, ...docPermissions])];
    } catch (error) {
      return [...new Set([...fallback, ...claimPermissions])];
    }
  };

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setState({
          isLoading: false,
          isAuthenticated: false,
          user: null,
          role: ROLES.USER,
          tenantId: null,
          permissions: [],
          claims: {},
        });
        return;
      }

      try {
        const tokenResult = await firebaseUser.getIdTokenResult(false);
        const claims = tokenResult?.claims || {};
        const fallbackRole = SUPER_ADMIN_UID_OVERRIDES.has(firebaseUser.uid)
          ? ROLES.SUPER_ADMIN
          : ROLES.USER;
        const resolvedRole = claims.role ? normalizeRole(claims.role) : fallbackRole;
        const resolvedTenantId = claims.tenantId || null;
        const permissions = await resolvePermissions({
          firebaseUser,
          claims,
          role: resolvedRole,
          tenantId: resolvedTenantId,
        });

        setState({
          isLoading: false,
          isAuthenticated: true,
          user: firebaseUser,
          role: resolvedRole,
          tenantId: resolvedTenantId,
          permissions,
          claims,
        });
      } catch (error) {
        setState({
          isLoading: false,
          isAuthenticated: true,
          user: firebaseUser,
          role: ROLES.USER,
          tenantId: null,
          permissions: [],
          claims: {},
        });
      }
    });

    return () => unsubscribe();
  }, []);

  const value = useMemo(() => {
    const isSuperAdmin = state.role === ROLES.SUPER_ADMIN;
    const isTenantAdmin = state.role === ROLES.TENANT_ADMIN;
    const isVisitor = state.role === ROLES.VISITOR;

    return {
      ...state,
      isSuperAdmin,
      isTenantAdmin,
      isVisitor,
      hasAnyRole: (allowedRoles = []) => {
        if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) return true;
        return allowedRoles.includes(state.role);
      },
      hasAllPermissions: (requiredPermissions = []) => {
        if (isSuperAdmin) return true;
        if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) return true;
        return requiredPermissions.every((permission) => state.permissions.includes(permission));
      },
      canAccessTenant: (resourceTenantId) => {
        if (isSuperAdmin) return true;
        if (!resourceTenantId || !state.tenantId) return false;
        return resourceTenantId === state.tenantId;
      },
    };
  }, [state]);

  return <AuthzContext.Provider value={value}>{children}</AuthzContext.Provider>;
}

AuthzProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAuthz() {
  const context = useContext(AuthzContext);
  if (!context) {
    throw new Error("useAuthz must be used within an AuthzProvider");
  }
  return context;
}



